import React, { useEffect, useState } from 'react';
import { adminChannelsApi, Channel } from '../../lib/api/channels';
import { Share2 } from 'lucide-react';
import { SafeImage } from '../../components/common/SafeImage';


export const ChannelsPage: React.FC = () => {
    const [channels, setChannels] = useState<Channel[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadChannels();
    }, []);

    const loadChannels = async () => {
        try {
            setLoading(true);
            const data = await adminChannelsApi.getAll();
            setChannels(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (channel: Channel, newValue: boolean) => {
        // Optimistic update
        setChannels(prev => prev.map(c => c.id === channel.id ? { ...c, isActive: newValue } : c));
        try {
            await adminChannelsApi.updateStatus(channel.id, newValue);
        } catch (err) {
            console.error('Failed to update channel status', err);
            // Revert
            setChannels(prev => prev.map(c => c.id === channel.id ? { ...c, isActive: !newValue } : c));
        }
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">

                {/* Toolbar */}
                <div className="flex items-center justify-between p-3 border-b border-neutral-200 bg-neutral-50/50">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-50 text-blue-600 flex items-center justify-center rounded-md">
                            <Share2 size={18} />
                        </div>
                        <div>
                            <span className="block text-sm font-semibold text-neutral-900">Channels Manager</span>
                            <span className="block text-xs text-neutral-500">Enable or disable available integrations</span>
                        </div>
                    </div>
                </div>

                {/* Header Grid */}
                <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    <div className="col-span-5">Channel</div>
                    <div className="col-span-3">Category</div>
                    <div className="col-span-2">ID</div>
                    <div className="col-span-2 text-right">Status</div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <p className="text-sm">Loading channels...</p>
                        </div>
                    ) : channels.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <p className="text-sm">No channels found</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {channels.map(channel => (
                                <div key={channel.id} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-neutral-50/50 transition-colors text-sm">
                                    <div className="col-span-5 flex items-center gap-3">
                                        {channel.icon && (channel.icon.startsWith('http') || channel.icon.startsWith('/')) ? (
                                            <SafeImage
                                                src={channel.icon}
                                                alt={channel.name}
                                                className="w-10 h-10 object-contain p-1 bg-white border border-neutral-200 rounded-md"
                                                fallback={
                                                    <div className="w-10 h-10 bg-neutral-100 rounded-md flex items-center justify-center text-neutral-400">
                                                        <Share2 size={20} />
                                                    </div>
                                                }
                                            />
                                        ) : channel.icon ? (

                                            <div className="w-10 h-10 bg-neutral-100 rounded-md flex items-center justify-center text-2xl">
                                                {channel.icon}
                                            </div>
                                        ) : (
                                            <div className="w-10 h-10 bg-neutral-100 rounded-md flex items-center justify-center text-neutral-400">
                                                <Share2 size={20} />
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-medium text-neutral-900">{channel.name}</div>
                                            {channel.description && <div className="text-xs text-neutral-500 truncate max-w-xs">{channel.description}</div>}
                                        </div>
                                    </div>
                                    <div className="col-span-3">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800 capitalize">
                                            {channel.category || 'General'}
                                        </span>
                                    </div>
                                    <div className="col-span-2">
                                        <code className="text-xs bg-neutral-50 px-1.5 py-0.5 rounded border border-neutral-200 text-neutral-600">{channel.id}</code>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={channel.isActive}
                                                onChange={(e) => handleToggle(channel, e.target.checked)}
                                            />
                                            <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
